/**
 * Channel-aware difficulty engine.
 *
 * Operates on the generic RhythmNote model. Each channel ("left", "right",
 * "lane0", etc.) gets its own per-beat curve covering:
 *   - speed:    spatial distance / Δt between consecutive same-channel notes
 *   - density:  notes-per-second in a sliding window
 *   - reach:    distance normalised by an editor-defined "max reach"
 *   - stamina:  exponential moving average of the instant load
 *   - difficulty: weighted blend, 0..1
 *
 * The "distance" function is editor-provided — Synth Rider uses 3D position
 * distance, Guitar Hero uses lane-jump distance, etc. The engine doesn't
 * care what dimension you're in, only that distances come back in the same
 * units as `maxReach`.
 *
 * The combined curve dominates by the busiest single channel and adds a
 * coordination tax when multiple channels fire simultaneously.
 */

import type { RhythmNote } from './types';

// ── Public types ─────────────────────────────────────────────────────────────

export interface ChannelMetrics {
  /** Per-beat speed (distance / second), windowed. */
  speed: number[];
  /** Per-beat density (notes/second), windowed. */
  density: number[];
  /** Per-beat reach normalised by `maxReach` (typically 0..1). */
  reach: number[];
  /** Per-beat stamina (EMA of instant load). */
  stamina: number[];
  /** Combined per-beat difficulty 0..1. */
  difficulty: number[];
  peak_speed: number;
  avg_speed: number;
  peak_density: number;
  avg_density: number;
  peak_reach: number;
  note_count: number;
}

export interface DifficultyReport {
  /** One ChannelMetrics block per channel found in the notes. */
  channels: Record<string, ChannelMetrics>;
  /** Aggregated per-beat difficulty across channels, 0..1. */
  combined: number[];
  /** 90th-percentile of `combined` → single overall score, 0..1. */
  overall_score: number;
  /** Map length in integer beats. */
  total_beats: number;
  bpm: number;
}

export interface DifficultyOptions {
  /** Tempo of the song. */
  bpm: number;
  /**
   * Function that returns the distance between two consecutive same-channel
   * notes. Synth Rider uses 3D world distance; Guitar Hero would use lane
   * delta. Units must match `maxReach`.
   */
  distanceBetween: (a: RhythmNote, b: RhythmNote, channel: string) => number;
  /**
   * Maximum meaningful distance — distances above this all count as
   * "fully stretched" (clipped to 1.0 in the reach metric).
   */
  maxReach: number;
  /** Smoothing window radius, in beats (default 2). */
  windowHalfBeats?: number;
  /** Saturation cap for speed normalisation in distance/second (default 7). */
  speedCap?: number;
  /** Saturation cap for density normalisation in notes/second (default 10). */
  densityCap?: number;
  /**
   * Optional override for how to split a note into channels. By default we
   * use `note.channels` directly; SR's "both_hands_special" maps to
   * ['left', 'right', 'both'] which the engine handles naturally.
   */
  channelsFor?: (note: RhythmNote) => string[];
  /**
   * Optional weights for combining channels. The aggregated `combined` curve
   * is computed as: max(perChannel) * primaryWeight + sum(otherChannels) * coordTax.
   * Both default to sensible values, but editors can tune.
   */
  primaryWeight?: number; // default 0.80
  coordTax?: number;      // default 0.30
}

// ── Implementation ───────────────────────────────────────────────────────────

const DEFAULT_WINDOW_HALF_BEATS = 2;
const DEFAULT_SPEED_CAP = 7;
const DEFAULT_DENSITY_CAP = 10;
const DEFAULT_PRIMARY_WEIGHT = 0.80;
const DEFAULT_COORD_TAX = 0.30;
const STAMINA_ALPHA = 0.18; // ~5-6 beat half life

function emptyChannelMetrics(totalBeats: number, noteCount: number): ChannelMetrics {
  const arr = () => new Array<number>(totalBeats).fill(0);
  return {
    speed: arr(),
    density: arr(),
    reach: arr(),
    stamina: arr(),
    difficulty: arr(),
    peak_speed: 0,
    avg_speed: 0,
    peak_density: 0,
    avg_density: 0,
    peak_reach: 0,
    note_count: noteCount,
  };
}

function computeChannelMetrics(
  notes: RhythmNote[],
  channel: string,
  totalBeats: number,
  opts: Required<Omit<DifficultyOptions, 'channelsFor'>>,
): ChannelMetrics {
  const metrics = emptyChannelMetrics(totalBeats, notes.length);
  if (notes.length === 0 || totalBeats <= 0) return metrics;

  const sorted = [...notes].sort((a, b) => a.time_beats - b.time_beats);
  const n = sorted.length;
  const beats = sorted.map(no => no.time_beats);
  const secs = sorted.map(no => no.time_seconds);

  // Per-note speed (distance/sec) and raw distance between consecutive notes.
  const speedPerNote = new Array<number>(n).fill(0);
  const distancePerNote = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    const dist = opts.distanceBetween(sorted[i - 1], sorted[i], channel);
    const dt = Math.max(secs[i] - secs[i - 1], 0.01);
    distancePerNote[i] = dist;
    speedPerNote[i] = dist / dt;
  }

  const windowDurationSeconds = (opts.windowHalfBeats * 2) * 60 / opts.bpm;
  const safeMaxReach = Math.max(opts.maxReach, 1e-6);

  // Per-beat windowed averages.
  for (let b = 0; b < totalBeats; b++) {
    const lo = b - opts.windowHalfBeats;
    const hi = b + opts.windowHalfBeats;
    let inWindow = 0;
    let speedSum = 0;
    let reachSum = 0;
    for (let i = 0; i < n; i++) {
      if (beats[i] >= lo && beats[i] <= hi) {
        inWindow++;
        speedSum += speedPerNote[i];
        reachSum += distancePerNote[i] / safeMaxReach;
      }
    }
    metrics.density[b] = inWindow / Math.max(windowDurationSeconds, 0.01);
    if (inWindow > 0) {
      metrics.speed[b] = speedSum / inWindow;
      metrics.reach[b] = Math.min(1.5, reachSum / inWindow);
    }
  }

  // Peaks / averages.
  metrics.peak_speed = Math.max(...metrics.speed);
  metrics.avg_speed = metrics.speed.reduce((a, b) => a + b, 0) / Math.max(metrics.speed.length, 1);
  metrics.peak_density = Math.max(...metrics.density);
  metrics.avg_density = metrics.density.reduce((a, b) => a + b, 0) / Math.max(metrics.density.length, 1);
  metrics.peak_reach = Math.max(...metrics.reach);

  // Normalised channels.
  const speedNorm = metrics.speed.map(v => Math.max(0, Math.min(1, v / opts.speedCap)));
  const densityNorm = metrics.density.map(v => Math.max(0, Math.min(1, v / opts.densityCap)));
  const reachNorm = metrics.reach.map(v => Math.max(0, Math.min(1, v)));

  // Stamina = EMA of instant load.
  const instantLoad = densityNorm.map((d, i) => 0.5 * d + 0.3 * speedNorm[i] + 0.2 * reachNorm[i]);
  let s = 0;
  for (let b = 0; b < totalBeats; b++) {
    s = STAMINA_ALPHA * instantLoad[b] + (1 - STAMINA_ALPHA) * s;
    metrics.stamina[b] = s;
  }

  // Final per-beat difficulty.
  for (let b = 0; b < totalBeats; b++) {
    const v = 0.40 * densityNorm[b] + 0.25 * speedNorm[b] + 0.20 * reachNorm[b] + 0.15 * metrics.stamina[b];
    metrics.difficulty[b] = Math.max(0, Math.min(1, v));
  }

  return metrics;
}

/** Main entry point — compute the full difficulty report for a list of notes. */
export function computeDifficulty(
  notes: RhythmNote[],
  options: DifficultyOptions,
): DifficultyReport {
  const opts: Required<Omit<DifficultyOptions, 'channelsFor'>> = {
    bpm: options.bpm,
    distanceBetween: options.distanceBetween,
    maxReach: options.maxReach,
    windowHalfBeats: options.windowHalfBeats ?? DEFAULT_WINDOW_HALF_BEATS,
    speedCap: options.speedCap ?? DEFAULT_SPEED_CAP,
    densityCap: options.densityCap ?? DEFAULT_DENSITY_CAP,
    primaryWeight: options.primaryWeight ?? DEFAULT_PRIMARY_WEIGHT,
    coordTax: options.coordTax ?? DEFAULT_COORD_TAX,
  };
  const channelsFor = options.channelsFor ?? ((note: RhythmNote) => note.channels);

  if (!notes.length || opts.bpm <= 0) {
    return { channels: {}, combined: [], overall_score: 0, total_beats: 0, bpm: opts.bpm };
  }

  // Bucket notes per channel.
  const byChannel = new Map<string, RhythmNote[]>();
  for (const note of notes) {
    for (const ch of channelsFor(note)) {
      if (!byChannel.has(ch)) byChannel.set(ch, []);
      byChannel.get(ch)!.push(note);
    }
  }

  const lastBeat = Math.max(...notes.map(n => n.time_beats));
  const totalBeats = Math.max(Math.ceil(lastBeat) + 1, 1);

  // Compute per-channel metrics.
  const channels: Record<string, ChannelMetrics> = {};
  for (const [ch, list] of byChannel) {
    channels[ch] = computeChannelMetrics(list, ch, totalBeats, opts);
  }

  // Combined per-beat curve — max of channels + coordination tax for overlap.
  const combined = new Array<number>(totalBeats).fill(0);
  for (let b = 0; b < totalBeats; b++) {
    let maxC = 0;
    let restSum = 0;
    let nonZeroCount = 0;
    for (const ch of Object.keys(channels)) {
      const v = channels[ch].difficulty[b];
      if (v > maxC) maxC = v;
      if (v > 0) nonZeroCount++;
    }
    for (const ch of Object.keys(channels)) {
      const v = channels[ch].difficulty[b];
      if (v < maxC) restSum += v;
    }
    const coord = nonZeroCount > 1 ? opts.coordTax * restSum : 0;
    combined[b] = Math.max(0, Math.min(1, opts.primaryWeight * maxC + coord));
  }

  // 90th percentile of combined → single overall score.
  const sorted = [...combined].sort((a, b) => a - b);
  const idx = Math.floor(sorted.length * 0.9);
  const overall_score = sorted[Math.min(idx, sorted.length - 1)] ?? 0;

  return { channels, combined, overall_score, total_beats: totalBeats, bpm: opts.bpm };
}

// ── Color ramp helper (shared visualisation) ─────────────────────────────────

/** Apply the editor's "contrast" sigmoid before mapping to color. */
export function contrastBoost(v: number, k: number = 5.5): number {
  const clipped = Math.max(0, Math.min(1, v));
  const raw = 1 / (1 + Math.exp(-(clipped - 0.5) * k));
  const lo = 1 / (1 + Math.exp(0.5 * k));
  const hi = 1 / (1 + Math.exp(-0.5 * k));
  return (raw - lo) / (hi - lo);
}

/** Green → amber → red gradient for difficulty visualisation. */
export function difficultyToColor(t: number, contrastK: number = 5.5): string {
  const v = contrastBoost(t, contrastK);
  if (v < 0.5) {
    const f = v / 0.5;
    const r = Math.round(0x97 + (0xEF - 0x97) * f);
    const g = Math.round(0xC4 + (0x9F - 0xC4) * f);
    const b = Math.round(0x59 + (0x27 - 0x59) * f);
    return `rgb(${r},${g},${b})`;
  }
  const f = (v - 0.5) / 0.5;
  const r = Math.round(0xEF + (0xE2 - 0xEF) * f);
  const g = Math.round(0x9F + (0x4B - 0x9F) * f);
  const b = Math.round(0x27 + (0x4A - 0x27) * f);
  return `rgb(${r},${g},${b})`;
}

/** Map a 0..1 score to a tier name. */
export function ratingFromScore(score: number): 'Easy' | 'Normal' | 'Hard' | 'Expert' | 'Master' | 'Insane' {
  if (score < 0.15) return 'Easy';
  if (score < 0.30) return 'Normal';
  if (score < 0.50) return 'Hard';
  if (score < 0.70) return 'Expert';
  if (score < 0.85) return 'Master';
  return 'Insane';
}
